import React, { useState } from 'react';
import {
  Alert, Box, Divider, FormControl, FormControlLabel, FormHelperText, FormLabel, InputLabel, MenuItem,
  Paper, Radio, RadioGroup, Select, TextField, Typography,
} from '@mui/material';
import { VpnKey as KeyIcon } from '@mui/icons-material';
import { CRON_PRESETS, SOURCE_TYPES, SYNC_POLICIES, emptySourceConfig } from '../utils/registrySourceConfig';
import { isSchemaFlagTrue, orderedPropertyEntries } from '../utils/registrySchemaUtils';
import { MONO_FONT } from '../constants/typography';
import { renderConnectionFields } from './SourceConnectionFields';

const CUSTOM_PRESET = CRON_PRESETS.find((p) => p.cron === null);
const SCHEDULED_TYPES = ['sap', 'crm-rest'];
const ERROR_PREFIX = 'sourceConfig.';
const monoInput = { sx: { fontFamily: MONO_FONT } };

// Titolo di una sotto-sezione: stesso aspetto per i gruppi di radio (FormLabel) e per gli altri blocchi.
const subheadingSx = { typography: 'subtitle2', color: 'text.primary', '&.Mui-focused': { color: 'text.primary' }, mb: 0.5 };

const isOwnError = (path) => path === 'sourceType' || path.startsWith(ERROR_PREFIX);

/** Radio con la riga di spiegazione sotto, collegata come descrizione accessibile. */
const HintedRadio = ({ value, label, hint, disabled, idPrefix }) => {
  const hintId = `${idPrefix}-${value}-hint`;
  return (
    <Box sx={{ mb: 0.5 }}>
      <FormControlLabel
        value={value}
        disabled={disabled}
        label={label}
        control={<Radio size="small" inputProps={hint ? { 'aria-describedby': hintId } : undefined} />}
      />
      {hint && <FormHelperText id={hintId} error={false} sx={{ mt: -0.75, ml: '27px' }}>{hint}</FormHelperText>}
    </Box>
  );
};

/**
 * Sezione "Sorgente dati" del dialog anagrafica: tipo, connessione, corrispondenza campi,
 * politica di aggiornamento e pianificazione. Componente controllato: ogni modifica emette
 * onChange({ sourceType, sourceConfig }) con il valore completo.
 */
export default function SourceConfigEditor({ sourceType, sourceConfig, schemaProperties, errors, onChange }) {
  // Unico stato locale: l'utente ha scelto "Personalizzato" anche se il cron coincide con un preset.
  const [customSchedule, setCustomSchedule] = useState(false);

  const config = sourceConfig || {};
  const source = config.source || {};
  const mapping = config.mapping || {};
  const sync = config.sync || {};
  const allErrors = errors || {};

  // Ogni campo reso registra il proprio percorso: gli errori senza campo finiscono nell'Alert in testa.
  const rendered = new Set();
  const fieldProps = (path, helperText) => {
    rendered.add(ERROR_PREFIX + path);
    const message = allErrors[ERROR_PREFIX + path];
    return { error: Boolean(message), helperText: message || helperText };
  };

  const emit = (nextConfig) => onChange({ sourceType, sourceConfig: nextConfig });
  const setSource = (patch) => emit({ ...config, source: { ...source, ...patch } });
  const setSync = (patch) => emit({ ...config, sync: { ...sync, ...patch } });
  const setMapping = (prop, value) => {
    const next = { ...mapping };
    if (value === '') delete next[prop];
    else next[prop] = value;
    emit({ ...config, mapping: next });
  };

  const changeType = (code) => {
    const empty = emptySourceConfig(code);
    const policy = sync.policy === 'delta' && code === 'csv' ? 'full-replace' : (sync.policy ?? 'full-replace');
    setCustomSchedule(false);
    onChange({
      sourceType: code,
      sourceConfig: { ...empty, mapping: config.mapping ?? {}, sync: { ...empty.sync, policy } },
    });
  };

  const isLegacy = Boolean(sourceType) && !SOURCE_TYPES.some((t) => t.code === sourceType);
  rendered.add('sourceType');
  const typeError = allErrors.sourceType;

  const connection = renderConnectionFields(sourceType, { source, setSource, fieldProps });

  const properties = orderedPropertyEntries({ properties: schemaProperties || {} });
  const mappingRows = properties.map(([prop, def]) => {
    const name = (def && def.name) || prop;
    return (
      <Box
        key={prop}
        data-testid="mapping-row"
        sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 200px) auto 1fr', columnGap: 1.5, alignItems: 'center' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography variant="body2" noWrap title={name}>{name}</Typography>
          {def && isSchemaFlagTrue(def.key) && (
            <KeyIcon titleAccess="Campo chiave" sx={{ fontSize: 16, color: 'primary.main' }} />
          )}
        </Box>
        <Typography aria-hidden color="text.secondary">←</Typography>
        <TextField
          size="small"
          fullWidth
          placeholder={prop}
          value={mapping[prop] ?? ''}
          onChange={(e) => setMapping(prop, e.target.value)}
          slotProps={{ htmlInput: { 'aria-label': `Campo sorgente per ${name}` }, input: monoInput }}
          {...fieldProps(`mapping.${prop}`)}
        />
      </Box>
    );
  });

  const policy = sync.policy || '';
  const deltaFields = policy === 'delta' && (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, mt: 1.5 }}>
      <TextField
        size="small" fullWidth label="Campo data di modifica"
        value={sync.updatedAtField ?? ''}
        onChange={(e) => setSync({ updatedAtField: e.target.value })}
        slotProps={{ input: monoInput }}
        {...fieldProps('sync.updatedAtField', 'Il campo della sorgente con la data dell’ultima modifica')}
      />
      {sourceType === 'sap' && (
        <TextField
          size="small" fullWidth label="Filtro delta"
          value={sync.deltaFilterTemplate ?? ''}
          onChange={(e) => setSync({ deltaFilterTemplate: e.target.value })}
          slotProps={{ input: monoInput }}
          {...fieldProps('sync.deltaFilterTemplate',
            'Usa {watermark} dove va la data, es. LastChangeDateTime ge datetimeoffset\'{watermark}\'')}
        />
      )}
      {sourceType === 'crm-rest' && (
        <TextField
          size="small" fullWidth label="Parametro delta"
          value={sync.deltaParam ?? ''}
          onChange={(e) => setSync({ deltaParam: e.target.value })}
          slotProps={{ input: monoInput }}
          {...fieldProps('sync.deltaParam', 'Nome del parametro query che riceve la data')}
        />
      )}
    </Box>
  );

  const scheduled = SCHEDULED_TYPES.includes(sourceType);
  const schedule = sync.schedule ?? '';
  const presetLabel = customSchedule
    ? CUSTOM_PRESET.label
    : (CRON_PRESETS.find((p) => p.cron !== null && p.cron === schedule) || CUSTOM_PRESET).label;
  const choosePreset = (label) => {
    const preset = CRON_PRESETS.find((p) => p.label === label);
    if (!preset || preset.cron === null) {
      setCustomSchedule(true);
      return;
    }
    setCustomSchedule(false);
    setSync({ schedule: preset.cron });
  };
  const scheduleFields = scheduled && (
    <Box sx={{ mt: 2 }}>
      <Typography sx={subheadingSx}>Quando</Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, alignItems: 'start' }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="source-schedule-preset-label">Frequenza</InputLabel>
          <Select
            labelId="source-schedule-preset-label"
            label="Frequenza"
            value={presetLabel}
            onChange={(e) => choosePreset(e.target.value)}
          >
            {CRON_PRESETS.map((p) => <MenuItem key={p.label} value={p.label}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" fullWidth label="Espressione cron"
          value={schedule}
          onChange={(e) => setSync({ schedule: e.target.value })}
          slotProps={{ input: monoInput }}
          {...fieldProps('sync.schedule', 'Orario UTC · 6 campi: secondi minuti ore giorno mese giorno-settimana')}
        />
      </Box>
    </Box>
  );

  const policyError = fieldProps('sync.policy');

  const unmapped = Object.entries(allErrors).filter(([path]) => isOwnError(path) && !rendered.has(path));

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" gutterBottom>Sorgente dati</Typography>

      {unmapped.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <div>Correggi:</div>
          {unmapped.map(([path, message]) => (
            <div key={path}>{`${path.replace(ERROR_PREFIX, '')} — ${message}`}</div>
          ))}
        </Alert>
      )}

      <FormControl component="fieldset" error={Boolean(typeError)} sx={{ display: 'block' }}>
        <FormLabel component="legend" sx={subheadingSx}>Tipo</FormLabel>
        <RadioGroup row value={sourceType || ''} onChange={(e) => changeType(e.target.value)}>
          {SOURCE_TYPES.map((t) => (
            <Box key={t.code} sx={{ mr: 3, maxWidth: 260 }}>
              <HintedRadio idPrefix="source-type" value={t.code} label={t.label} hint={t.hint} />
            </Box>
          ))}
          {isLegacy && (
            <HintedRadio idPrefix="source-type" value={sourceType} label={`${sourceType} (tipo precedente)`} disabled />
          )}
        </RadioGroup>
        {typeError && <FormHelperText>{typeError}</FormHelperText>}
      </FormControl>

      {connection && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={subheadingSx}>Connessione</Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>{connection}</Paper>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography sx={subheadingSx}>Corrispondenza campi</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Per ogni campo dell’anagrafica indica il campo della sorgente da cui leggerlo. Se lo lasci vuoto si usa
          lo stesso nome.
        </Typography>
        {mappingRows.length > 0 ? (
          <Box sx={{ display: 'grid', rowGap: 1 }}>{mappingRows}</Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            L’anagrafica non ha ancora campi: aggiungili in Campi anagrafica.
          </Typography>
        )}
      </Box>

      <FormControl component="fieldset" error={policyError.error} sx={{ display: 'block', mt: 2 }}>
        <FormLabel component="legend" sx={subheadingSx}>Come aggiornare le voci</FormLabel>
        <RadioGroup value={policy} onChange={(e) => setSync({ policy: e.target.value })}>
          {SYNC_POLICIES.map((p) => (
            <HintedRadio
              key={p.code} idPrefix="sync-policy" value={p.code} label={p.label} hint={p.hint}
              disabled={p.code === 'delta' && sourceType === 'csv'}
            />
          ))}
        </RadioGroup>
        {policyError.error && <FormHelperText>{policyError.helperText}</FormHelperText>}
      </FormControl>
      {deltaFields}

      {scheduleFields}
    </Box>
  );
}
